import { DataTestConfig } from '@headless-workspace/config';
import { Component } from '@headless-workspace/typings';
import { StringHelpers } from '@headless-workspace/utils';
import { GlowTextBody, GlowTextCaption } from '../../atoms';
import { PropsWithFontFamily } from '../../props';

export type ProductPricesProps = PropsWithFontFamily & {
    minPrice?: string;
    price?: string;
    priceBeforeDiscount?: string;
    pricePerUnit?: string;
    minimalProductPriceLabel?: string;
    basePriceLabel?: string;
    priceAlwaysBlack: boolean;
    discountPercentageLabel?: string;
    totalIndividualPriceLabel?: string;
    legalMentionsLabel?: string;
};

// Component used only to display the price of a product in the Product Tile
export const ProductPrices: Component<ProductPricesProps> = ({
    minPrice,
    price,
    priceBeforeDiscount,
    pricePerUnit,
    fontFamily,
    minimalProductPriceLabel,
    basePriceLabel,
    priceAlwaysBlack,
    discountPercentageLabel,
    totalIndividualPriceLabel,
    legalMentionsLabel,
}) => {
    return (
        <div className={'flex flex-col gap-0.25'}>
            <div>
                {!minPrice && !!price && (
                    <GlowTextBody
                        text={legalMentionsLabel ? StringHelpers.addAsterisk(price) : price}
                        fontWeight={'bold'}
                        fontFamily={fontFamily}
                        color={!priceAlwaysBlack && priceBeforeDiscount ? 'promotion' : 'primary'}
                        dataTestId={DataTestConfig.testIds.productTilePrice}
                    />
                )}
                {!!minPrice && (
                    <div className={'flex items-center gap-0.25'}>
                        <GlowTextCaption text={minimalProductPriceLabel} fontFamily={fontFamily} />
                        <GlowTextBody
                            text={legalMentionsLabel ? StringHelpers.addAsterisk(minPrice) : minPrice}
                            fontWeight={'bold'}
                            fontFamily={fontFamily}
                            color={!priceAlwaysBlack && priceBeforeDiscount ? 'promotion' : 'primary'}
                            dataTestId={DataTestConfig.testIds.productTilePrice}
                        />
                    </div>
                )}
                <div className={'flex flex-col gap-0.25 tablet:gap-0.5'}>
                    <GlowTextCaption
                        text={
                            legalMentionsLabel && pricePerUnit
                                ? StringHelpers.removeAsterisk(pricePerUnit)
                                : pricePerUnit
                        }
                        dataTestId={'price-per-unit'}
                        color={'tertiary'}
                        fontFamily={fontFamily}
                    />
                    {priceBeforeDiscount && !!price && (
                        <div className={'flex flex-wrap gap-0.25'}>
                            {basePriceLabel && (
                                <GlowTextCaption
                                    text={basePriceLabel}
                                    fontFamily={fontFamily}
                                    color={'primary'}
                                    className={'whitespace-break-spaces'}
                                />
                            )}
                            <div className={'flex flex-nowrap gap-0.25'}>
                                <GlowTextCaption
                                    text={priceBeforeDiscount}
                                    fontFamily={fontFamily}
                                    color={'primary'}
                                    className={'whitespace-break-spaces'}
                                />
                                <GlowTextCaption
                                    className={'px-0.25 bg-background-error-red rounded-0.25'}
                                    color={'promotion'}
                                    text={discountPercentageLabel}
                                    fontWeight={'bold'}
                                    fontFamily={fontFamily}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <GlowTextCaption
                className={'line-clamp-1'}
                text={totalIndividualPriceLabel}
                color={'primary'}
                fontFamily={fontFamily}
            />
        </div>
    );
};
